import React, { useEffect, useRef, useState } from 'react';
import './system-events.scss';

import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import fileDownload from 'js-file-download';
import { useIntl } from 'react-intl';
import {
  getJsonFromStorage,
  getPaginationOpts,
  responseLogWithNotification,
  useUpdatePeriod,
} from '../../../utils/utils';
import { downloadSystemEvents, loadSystemEvents } from '../actions';
import { getSystemData } from '../../System/reducer';
import Loader from '../../../components/base/Loader/Loader';
import FilterRow from '../../../components/base/FilterRow/FilterRow';
import { getSystemEventsData } from '../reducer';
import TextInput from '../../../components/fields/TextInput/TextInput';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import DatePicker from '../../../components/fields/DatePicker/DatePicker';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import Pagination from '../../../components/base/Pagination/Pagination';
import {
  checkBoxFormatter,
  dateTimeFormatter,
  tooltipFormatter,
} from '../../../utils/formatters';
import SystemEventsFormDTO from '../../../dto/SystemEventsFormDTO';
import { errorNotification } from '../../Notifications/notificationDecorator';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';

export default function SystemEventsPage() {
  const blockRef = useRef(null);
  const dispatch = useDispatch();
  const [selected, setSelected] = useState([]);
  const [filter, setFilter] = useState({});
  const intl = useIntl();
  const { formatMessage } = intl;
  const {
    loadingSystemEvents, systemEvents = [], systemEventsMeta = {}, downloadingSystemEvents,
  } = useSelector(getSystemEventsData);
  const { updatePeriod } = useSelector(getSystemData);
  const DTO = new SystemEventsFormDTO();
  const attributes = DTO.getAttributesByName();

  const loadData = opts => {
    const paginationOpts = getPaginationOpts(systemEventsMeta, opts);
    const requestData = {
      ...paginationOpts,
      filter,
      reverse: true,
      // sort: sortData.join(','),
    };
    const p1 = dispatch(loadSystemEvents(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };
  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  useEffect(() => { loadData(); }, [filter]);

  const defaultFilterValues = {
    find: '',
    date: '',
    module: '',
    level: '',
  };

  const {
    register, getValues, watch, setValue, reset, control,
  } = useForm({
    defaultValues: getJsonFromStorage('system-events-filter') || { ...defaultFilterValues },
  });
  watch(data => setFilter(data));
  const headerCheckBoxFormatter = (id, item) => {
    const isAll = systemEvents.every(p => selected.includes(p.id));
    const isSome = systemEvents.some(p => selected.includes(p.id)) && !isAll;
    const onChange = () => {
      if (isAll) {
        setSelected([]);
      } else {
        setSelected(systemEvents.map(r => r.id));
      }
    };
    return checkBoxFormatter(isAll, onChange, classNames({ 'some-child': isSome }, { 'every-child': isAll }));
  };

  const rowCheckBoxFormatter = (_, item) => {
    const onChange = () => {
      const index = selected.indexOf(item.id);
      if (index >= 0) {
        const newSelected = [...selected];
        newSelected.splice(index, 1);
        setSelected(newSelected);
      } else {
        setSelected([...selected, item.id]);
      }
    };
    return checkBoxFormatter(selected.includes(item.id), onChange);
  };

  const systemEventsListColumns = [
    {
      dataField: 'checkbox',
      text: formatMessage({ id: 'select.row' }),
      disableSort: true,
      headerFormatter: headerCheckBoxFormatter,
      formatter: rowCheckBoxFormatter,
      dataClassName: 'checkbox-column',
      headerClassName: 'checkbox-column',
    },
    {
      dataField: 'date',
      text: formatMessage({ id: 'session.start.time' }),
      formatter: dateTimeFormatter,
      headerClassName: 'date-column',
      disableSort: true,
    },
    {
      dataField: 'module',
      text: formatMessage({ id: 'module' }),
      headerClassName: 'module-column',
      disableSort: true,
    },
    {
      dataField: 'level',
      text: formatMessage({ id: 'level' }),
      headerClassName: 'level-column',
      disableSort: true,
    },
    {
      dataField: 'description',
      text: formatMessage({ id: 'event.description' }),
      formatter: tooltipFormatter,
      headerClassName: 'description-column',
      disableSort: true,
    },
  ];

  const downloadFile = () => {
    dispatch(downloadSystemEvents(selected))
      .then(({ data, timestamp }) => {
        if (data) {
          fileDownload(data, `system-events-${timestamp}.zip`);
        }
      })
      .catch(() => { errorNotification(formatMessage({ id: 'event.log.download' })); });
  };

  const moduleList = [
    { value: 0, name: 'Dashboard' },
    { value: 1, name: 'Detection' },
    { value: 2, name: 'Session' },
    { value: 3, name: 'Zone' },
    { value: 4, name: 'Log' },
    { value: 5, name: 'Settings' },
    { value: 6, name: 'Metrology' },
  ];

  const levelList = [
    { value: 0, name: 'Info' },
    { value: 1, name: 'Debug' },
    { value: 2, name: 'Warning' },
    { value: 3, name: 'Error' },
  ];

  return (
    <div className="page session-page">
      <Loader loading={loadingSystemEvents || downloadingSystemEvents} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.system_events' })}</div>
        <FilterRow>
          <FieldDTO
            control={control}
            component={TextInput}
            dtoObject={attributes.find}
            className="no-border"
            inputProps={{ ...register('find') }}
            inputBefore={(
              <div className="search-wrapper">
                <Icons customIcon className="search" width={24} height={24} icon="search" />
              </div>
            )}
          />
          <FieldDTO
            control={control}
            component={DatePicker}
            dtoObject={attributes.date}
            hideSmallButtons
            showArrowIcon
            usePortal
            className="no-border"
          />
          <FieldDTO
            control={control}
            component={CustomSelectInput}
            dtoObject={attributes.module}
            options={moduleList}
          />
          <FieldDTO
            control={control}
            component={CustomSelectInput}
            dtoObject={attributes.level}
            options={levelList}
          />
          <div className="filter-sep" />
          <Button onClick={downloadFile} variant="second" className=""><Icons customIcon className="file-download" width={24} height={24} icon="download" /></Button>
        </FilterRow>
      </div>
      <div className="page-content-wrapper">
        <div className="page-content" ref={blockRef}>
          <div className="grid-wrapper">
            <div className="custom-scroll-theme sticky-table-wrapper">
              <CustomizedGrid data={systemEvents} columns={systemEventsListColumns} name="system-event-list" settingsFormatter={(_, item) => (<Icons customIcon icon="dot" className="more-vert" />)} />
            </div>
          </div>
          <Pagination meta={systemEventsMeta} list={systemEvents} handleChangeMeta={loadData} />
        </div>
      </div>
    </div>
  );
}
